import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import DoneIcon from "@material-ui/icons/Done";
import ErrorIcon from "@material-ui/icons/Error";
import BlockIcon from "@material-ui/icons/Block";
import { makeStyles } from "@material-ui/core/styles";
import URLSearchParams from "@ungap/url-search-params";

import Divider from "@material-ui/core/Divider";
import { FormatAmount, FormatDate } from "../../../utils/Formatter";
import { DropReason, Status } from "../../../model/Charge";
import TestIndicator from "../../common/TestIndicator";

const useStyles = makeStyles(({ palette }) => {
  return {
    root: {
      color: palette.primary.contrastText,
      background: `linear-gradient(36deg, ${palette.primary.dark} 0%, ${palette.primary.main} 54%, ${palette.primary.light} 100%)`,
      padding: "25px",
      alignItems: "center",
      display: "flex",
      position: "relative",
      "& svg": {
        fontSize: "2rem",
        padding: "10px"
      }
    }
  };
});
const PaymentResult = ({ charge }) => {
  const formRef = useRef(null);
  const classes = useStyles();

  const getIcon = status => {
    switch (status) {
      case Status.PAID:
      case Status.LATE_SPEND:
      case Status.DOUBLE_SPEND:
      case Status.REFUNDED:
      case Status.REFUNDED_PARTIALLY:
      case Status.PROVISIONED:
        return <DoneIcon />;
      case Status.DROPPED:
        return <BlockIcon />;
      default:
        return <ErrorIcon />;
    }
  };

  const getMessage = status => {
    switch (status) {
      case Status.PAID:
      case Status.LATE_SPEND:
      case Status.DOUBLE_SPEND:
      case Status.REFUNDED:
      case Status.REFUNDED_PARTIALLY:
        return "Successful payment";
      case Status.DROPPED:
        return "The charge has been cancelled";
      case Status.PROVISIONED:
        return "Payment provisioned";
      default:
        return "An unknown error occured. Payment state is inconsistent.";
    }
  };

  const getDroppedChargeMessage = dropReason => {
    switch (dropReason) {
      case DropReason.TOO_MANY_ATTEMPTS:
        return "Too many failed attempts";
      case DropReason.EXPIRED:
        return "Your payment has expired";
      case DropReason.DROPPED_OUT:
        return "Charge cancelled by the merchant";
      default:
        return "";
    }
  };

  const getReturnFields = () => {
    const returnFields = [];
    const returnData = charge.formDataReturnData;
    if (charge.returnUrl) {
      const urlArray = charge.returnUrl.split("?");
      const [, queryParams] = urlArray;
      if (queryParams) {
        const searchParams = new URLSearchParams(queryParams);
        searchParams.forEach((value, key) =>
          returnFields.push(
            <input type="hidden" name={key} value={value} key={value} />
          )
        );
      }
    }

    if (!returnData) {
      return returnFields;
    }
    Object.entries(returnData).forEach(field => {
      if (field[1]) {
        returnFields.push(
          <input
            type="hidden"
            name={field[0]}
            value={field[1]}
            key={field[0]}
          />
        );
      }
    });
    return returnFields;
  };

  const startFormAutoSubmitionTimeout = delay => {
    setTimeout(() => {
      formRef.current.submit();
    }, delay * 1000);
  };

  useEffect(() => {
    let event;
    switch (charge.status) {
      case Status.PAID:
        event = { type: "lpg.charge.paid", charge };
        if (charge.redirectSuccessTimeout) {
          startFormAutoSubmitionTimeout(charge.redirectSuccessTimeout);
        }
        break;
      case Status.DROPPED:
        event = { type: "lpg.charge.dropped", charge };
        if (charge.redirectErrorTimeout) {
          startFormAutoSubmitionTimeout(charge.redirectErrorTimeout);
        }
        break;
      default:
        break;
    }
    // targetOrigin is '*' because the target is the merchant's website, so that could be anything.
    if (event && window.parent) {
      window.parent.postMessage(event, "*");
    }
  }, []);

  function getAmount() {
    return (
      <TableRow>
        <TableCell>Amount:</TableCell>
        <TableCell>
          {charge.status !== Status.DROPPED
            ? FormatAmount(charge.saleTrnsAmount, charge.currency)
            : FormatAmount(charge.due, charge.currency)}
          {charge.paymentFamilies.surcharges && (
            <div style={{ fontSize: "0.8rem" }}>
              {`Inclusive of ${FormatAmount(
                charge.fee + charge.tax,
                charge.currency
              )} (convenience fee + GST)`}
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  }

  function getFinalAmount() {
    return (
      <TableRow>
        <TableCell>Final amount:</TableCell>
        <TableCell>
          {charge.status !== Status.DROPPED &&
            FormatAmount(charge.saleTrnsAmount, charge.currency)}
          {charge.paymentFamilies.surcharges && (
            <div style={{ fontSize: "0.8rem" }}>
              {`Inclusive of ${FormatAmount(
                charge.fee + charge.tax,
                charge.currency
              )} (convenience fee + GST)`}
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  }

  function getProvisionedFinalAmount() {
    return (
      <>
        <TableRow>
          <TableCell>Provisioned amount:</TableCell>
          <TableCell>
            {FormatAmount(charge.provisioned, charge.currency)}
          </TableCell>
        </TableRow>
        {getFinalAmount()}
      </>
    );
  }

  function getProvisionedAmount() {
    return (
      <>
        <TableRow>
          <TableCell>Provisioned amount:</TableCell>
          <TableCell>
            {FormatAmount(charge.provisionedTrnsAmount, charge.currency)}
          </TableCell>
        </TableRow>
      </>
    );
  }

  function getProvisionedData() {
    return charge.status === Status.PROVISIONED
      ? getProvisionedAmount()
      : getProvisionedFinalAmount();
  }

  return (
    <Grid container direction="column">
      <div className={classes.root}>
        {getIcon(charge.status)}
        <div>
          <Typography variant="h6" align="center">
            {getMessage(charge.status)}
          </Typography>
          {charge.status === Status.DROPPED && (
            <Typography variant="caption" align="center">
              {getDroppedChargeMessage(charge.dropReason)}
            </Typography>
          )}
        </div>
        {charge.testMode && <TestIndicator />}
      </div>
      {charge.status === Status.PROVISIONED && (
        <div>
          <Grid
            container
            justifyContent="center"
            style={{
              color: "red",
              paddingLeft: "1.1rem",
              paddingRight: "1.1rem",
              paddingTop: "0.5rem",
              paddingBottom: "0.5rem"
            }}
          >
            The amount below is provisioned from your bank account but not
            debited yet. It will get debited only after merchant confirmation.
          </Grid>
          <Grid
            container
            alignItems="center"
            style={{
              height: "1px",
              backgroundColor: "rgba(224, 224, 224, 1)"
            }}
          >
            <Divider orientation="vertical" flexItem />
          </Grid>
        </div>
      )}
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>Shop Name:</TableCell>
            <TableCell>{charge.shopName}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Shop ID:</TableCell>
            <TableCell>{charge.shopId}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Order ID:</TableCell>
            <TableCell>{charge.orderId}</TableCell>
          </TableRow>
          {charge.provisioned !== 0 ? getProvisionedData() : getAmount()}

          {charge.refunded ? (
            <TableRow>
              <TableCell>Refunded:</TableCell>
              <TableCell>
                {FormatAmount(charge.refunded, charge.currency)}
              </TableCell>
            </TableRow>
          ) : (
            ""
          )}
          {charge.status !== Status.DROPPED ? (
            <TableRow>
              <TableCell>Payment Mode:</TableCell>
              <TableCell>{charge.paymentFamily}</TableCell>
            </TableRow>
          ) : (
            ""
          )}
          <TableRow>
            <TableCell>Date:</TableCell>
            <TableCell>{FormatDate(charge.date)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {charge.returnUrl && charge.returnUrl.trim() !== "" && (
        <Grid container justifyContent="center">
          <form
            action={charge.returnUrl}
            method={charge.returnMethod}
            ref={formRef}
          >
            {getReturnFields()}
            <Button
              color="primary"
              type="submit"
              style={{ marginTop: "15px", marginBottom: "15px" }}
            >
              Return to shop
            </Button>
          </form>
        </Grid>
      )}
    </Grid>
  );
};

PaymentResult.propTypes = {
  charge: PropTypes.shape({
    status: PropTypes.string.isRequired,
    shopName: PropTypes.string.isRequired,
    shopId: PropTypes.string.isRequired,
    orderId: PropTypes.string.isRequired,
    due: PropTypes.number.isRequired,
    paid: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    refunded: PropTypes.number,
    returnUrl: PropTypes.string.isRequired,
    returnMethod: PropTypes.string.isRequired,
    formDataReturnData: PropTypes.shape().isRequired,
    returnParamJson: PropTypes.string.isRequired,
    redirectSuccessTimeout: PropTypes.string,
    redirectErrorTimeout: PropTypes.string,
    dropReason: PropTypes.string,
    testMode: PropTypes.bool.isRequired,
    paymentFamily: PropTypes.string,
    paymentFamilies: PropTypes.shape({
      surcharges: PropTypes.arrayOf(PropTypes.shape()),
      fees: PropTypes.arrayOf(PropTypes.shape())
    }).isRequired,
    saleTrnsAmount: PropTypes.number.isRequired,
    provisioned: PropTypes.number.isRequired,
    fee: PropTypes.number.isRequired,
    tax: PropTypes.number.isRequired,
    provisionedTrnsAmount: PropTypes.number.isRequired
  }).isRequired
};

export default PaymentResult;
